export interface ErroApi {
  erro: string;
  detalhes?: unknown;
}

export class FalhaApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhes?: unknown
  ) {
    super(mensagem);
    this.name = "FalhaApi";
  }
}

async function requisitar<T>(caminho: string, init?: RequestInit): Promise<T> {
  const resposta = await fetch(`/api${caminho}`, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
    // O cookie de sessão é httpOnly; "same-origin" garante que ele acompanhe a requisição.
    credentials: "same-origin",
  });

  if (resposta.status === 204) return undefined as T;

  const corpo = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    const erro = (corpo as ErroApi | null)?.erro ?? "Falha na comunicação com o servidor";
    throw new FalhaApi(resposta.status, erro, (corpo as ErroApi | null)?.detalhes);
  }

  return corpo as T;
}

export interface UsuarioSessao {
  id: string;
  nome: string;
  email: string;
  papel: "ADMIN" | "MEMBRO";
  familiaId: string;
  familiaNome: string;
}

export type CategoriaLembrete = "GERAL" | "IMPORTANTE";
export type StatusLembreteApi = "ATIVO" | "CANCELADO" | "CONCLUIDO";
export type OrigemLembrete = "WHATSAPP" | "WEB";
export type TipoAlerta = "D2" | "D1" | "D0";
export type StatusEnvio = "PENDENTE" | "ENVIADO" | "FALHOU" | "CANCELADO";

export interface AlertaLembreteApi {
  id: string;
  tipo: TipoAlerta;
  previstoPara: string;
  status: StatusEnvio;
}

export interface LembreteApi {
  id: string;
  titulo: string;
  /** ISO datetime completo (o Prisma serializa @db.Date assim) — recorte pra "YYYY-MM-DD" na UI. */
  data: string;
  hora: string | null;
  categoria: CategoriaLembrete;
  observacoes: string | null;
  status: StatusLembreteApi;
  origem: OrigemLembrete;
  alertas: AlertaLembreteApi[];
}

export type StatusGrupoWhatsapp = "NAO_CRIADO" | "CRIANDO" | "CRIADO" | "ERRO";

export interface GrupoWhatsappApi {
  id: string;
  whatsappGroupId: string | null;
  linkConvite: string | null;
  status: StatusGrupoWhatsapp;
  ultimoErro: string | null;
}

export interface DadosLembrete {
  titulo: string;
  data: string; // "YYYY-MM-DD"
  hora?: string | null;
  categoria: CategoriaLembrete;
  observacoes?: string | null;
}

export const api = {
  cadastrar: (nomeFamilia: string, nomeAdmin: string, email: string, senha: string) =>
    requisitar<{ mensagem: string }>("/auth/cadastro", {
      method: "POST",
      body: JSON.stringify({ nomeFamilia, nomeAdmin, email, senha }),
    }),

  verificarEmail: (token: string) =>
    requisitar<{ usuario: UsuarioSessao }>(`/auth/verificar-email?token=${encodeURIComponent(token)}`),

  login: (email: string, senha: string) =>
    requisitar<{ usuario: UsuarioSessao }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, senha }),
    }),

  logout: () => requisitar<void>("/auth/logout", { method: "POST" }),

  eu: () => requisitar<{ usuario: UsuarioSessao }>("/auth/eu"),

  trocarSenha: (senhaAtual: string, novaSenha: string) =>
    requisitar<void>("/auth/trocar-senha", {
      method: "POST",
      body: JSON.stringify({ senhaAtual, novaSenha }),
    }),

  listarLembretes: () => requisitar<{ lembretes: LembreteApi[] }>("/lembretes"),

  criarLembrete: (dados: DadosLembrete) =>
    requisitar<{ lembrete: LembreteApi }>("/lembretes", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  atualizarLembrete: (id: string, dados: Partial<DadosLembrete>) =>
    requisitar<{ lembrete: LembreteApi }>(`/lembretes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  excluirLembrete: (id: string) => requisitar<void>(`/lembretes/${id}`, { method: "DELETE" }),

  obterGrupo: () => requisitar<{ grupo: GrupoWhatsappApi | null }>("/whatsapp/grupo"),

  criarGrupo: () => requisitar<{ grupo: GrupoWhatsappApi }>("/whatsapp/grupo", { method: "POST" }),
};
